<template>
  <div class="folder-list-container select-folder-modal-content copy-assets-destination-view">
    <div class="folder-sticky-header">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div class="search-container flex-grow-1">
          <div class="search-input-wrapper">
            <input
              v-model="searchKey"
              type="text"
              class="search-input"
              placeholder="Search..."
            >
            <svg
              width="13"
              height="13"
              class="search-icon"
            >
              <use href="/img/icons/sprite.svg#search-big" />
            </svg>
            <button
              v-if="searchKey"
              class="clear-search-btn"
              @click="clearSearch"
            >
              <i class="cil-x" />
            </button>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-between align-items-center mb-3">
        <BreadcrumbItem
          :breadcrumbs="breadcrumbs"
          @goTo="goToFolder"
        />
      </div>
    </div>

    <div class="folder-scroll-area">
      <template v-if="isListLoading">
        <div class="folders-grid">
          <div
            v-for="n in pageSize"
            :key="n"
            class="w-100 h-100"
            style="min-height: 167px;"
          >
            <b-skeleton
              width="100%"
              height="88%"
            />
            <b-skeleton />
          </div>
        </div>
      </template>

      <template v-else-if="paginatedFolders.length || fileItems.length">
        <div
          v-if="paginatedFolders.length"
          class="section-container"
        >
          <ItemGrid
            class="folder-items folders-grid"
            :items="paginatedFolders"
            :is-selected="(folder) => selectedFolderId === folder.id"
            :columns="4"
            :gap="16"
            :item-props="{ isAvailableOverlay: false }"
            @item-click="selectFolder"
            @item-dblclick="(folder) => navigateToFolder(folder.id)"
          />
        </div>

        <div
          v-if="isSaveMode && fileItems.length"
          class="section-container"
        >
          <ItemGrid
            class="folders-grid"
            :items="fileItems"
            :columns="4"
            :gap="16"
            :item-props="{ isAvailableOverlay: false }"
          />
        </div>
      </template>

      <template v-else>
        <b-alert
          show
          variant="warning"
        >
          {{ searchKey ? `No folders found matching "${searchKey}"` : 'This folder is empty' }}
        </b-alert>
      </template>
    </div>

    <div
      v-if="showPagination"
      class="folder-sticky-footer"
    >
      <div class="d-flex justify-content-center align-items-center item-pagination">
        <ItemPagination
          v-model="currentPage"
          :total-rows="paginationTotalRows"
          :per-page="pageSize"
          :disabled="isListLoading"
          @change="onPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ItemPagination from "@frontend/components/modules/file-browser/browser/ItemPagination.vue";
import BreadcrumbItem from '../../../common/atoms/BreadcrumbItem.vue';
import ItemGrid from "@frontend/components/modules/file-browser/browser/ItemGrid.vue";
import { FILE_TYPE } from '../../../../constants/file-image-types';
import { TABS } from '../constants';
import FileBrowser from '../../../../services/api/file-browser';

export default {
    name: 'CopyAssetsDestinationView',
    components: {
    ItemPagination,
        BreadcrumbItem,
        ItemGrid,
    },
    props: {
        currentFolderId: {
            type: [String, Number],
            default: null
        },
        currentTab: {
            type: String,
            default: null
        },
        isSaveMode: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            selectedFolderId: null,
            currentPage: 1,
            abortController: null,
            isListLoading: false,
            folderItems: [],
            fileItems: [],
            breadcrumbs: [],
            pageSize: 18,
            internalFolderId: null,
            searchKey: '',
            totalCount: 0,
        }
    },
    computed: {
        activeFolderId() {
            return this.internalFolderId !== null ? this.internalFolderId : this.currentFolderId;
        },

        useServerPagination() {
            return this.isSaveMode && !!this.activeFolderId;
        },

        filteredFolders() {
            if (this.useServerPagination || !this.searchKey || !this.searchKey.trim()) {
                return this.sortedFolderItems;
            }

            const searchTerm = this.searchKey.toLowerCase().trim();
            return this.sortedFolderItems.filter(folder =>
                folder.name.toLowerCase().includes(searchTerm)
            );
        },

        paginatedFolders() {
            if (this.useServerPagination) {
                return this.filteredFolders;
            }

            const start = (this.currentPage - 1) * this.pageSize;
            const end = start + this.pageSize;
            return this.filteredFolders.slice(start, end);
        },

        paginationTotalRows() {
            return this.useServerPagination ? this.totalCount : this.filteredFolders.length;
        },

        showPagination() {
            return this.paginationTotalRows > this.pageSize;
        },

        sortedFolderItems() {
            if (!this.folderItems.length) return [];

            return [...this.folderItems].sort((a, b) => {
                const aValue = (a.name || '').toLowerCase();
                const bValue = (b.name || '').toLowerCase();

                if (aValue > bValue) return 1;
                if (aValue < bValue) return -1;
                return 0;
            });
        }
    },
    watch: {
        currentFolderId: {
            immediate: true,
            handler(newVal, oldVal) {
                if (oldVal !== undefined && newVal !== oldVal) {
                    this.resetToFolder(newVal);
                }
            }
        },
        currentTab(newVal, oldVal) {
            if (oldVal !== undefined && newVal !== oldVal) {
                this.resetToFolder(this.currentFolderId);
            }
        },
        isSaveMode(newVal, oldVal) {
            if (oldVal !== undefined && newVal !== oldVal) {
                this.resetToFolder(this.currentFolderId);
            }
        },
        searchKey() {
            this.currentPage = 1;
            if (this.useServerPagination) {
                this.loadFolders();
            }
        }
    },
    mounted() {
        this.internalFolderId = this.currentFolderId;
        this.loadFolders();
    },
    beforeDestroy() {
        if (this.abortController) {
            this.abortController.abort();
        }
    },
    methods: {
        reload() {
            return this.loadFolders();
        },

        onPageChange(page) {
            this.currentPage = page;
            if (this.useServerPagination) {
                this.loadFolders();
            }
        },

        resetToFolder(folderId) {
            this.internalFolderId = folderId;
            this.breadcrumbs = [];
            this.selectedFolderId = null;
            this.currentPage = 1;
            this.fileItems = [];
            this.$emit('folder-selected', null);
            this.loadFolders();
        },

        clearSearch() {
            this.searchKey = '';
            this.currentPage = 1;
            if (this.useServerPagination) {
                this.loadFolders();
            }
        },

        parseFolderContentItems(items = []) {
            const folders = [];
            const files = [];

            items.forEach((item) => {
                if (item.name === '...') {
                    return;
                }

                if (item.type === FILE_TYPE.FOLDER || item.type === 'folder') {
                    folders.push({
                        ...item,
                        type: FILE_TYPE.FOLDER,
                    });
                    return;
                }

                files.push(item);
            });

            return { folders, files };
        },

        async loadFolders() {
            if (this.abortController) {
                this.abortController.abort();
            }

            this.abortController = new AbortController();

            this.isListLoading = true;

            try {
                let data;

                if (this.activeFolderId) {
                    let response;

                    if (this.isSaveMode) {
                        response = await axios.get('/file/folder_content', {
                            params: {
                                folderId: this.activeFolderId,
                                pageNumber: this.currentPage,
                                pageSize: this.pageSize,
                                type: 'image,png,jpg,jpeg,webp,gif,psd',
                                searchKey: this.searchKey.trim() || '',
                            },
                        });
                    } else if (this.currentTab === TABS.FILES) {
                        response = await FileBrowser.getFileFolderContent(this.activeFolderId, this.currentPage);
                    } else {
                        response = await FileBrowser.getFolderContent(this.activeFolderId, this.currentPage);
                    }

                    data = response.data;
                } else {
                    const params = {
                        pageSize: 1000,
                        pageNumber: 1,
                    };

                    let response;

                    if (this.isSaveMode) {
                        response = await FileBrowser.getFolderList(params);
                    } else if (this.currentTab === TABS.FILES) {
                        response = await FileBrowser.getFileFolderList(params);
                    } else {
                        response = await FileBrowser.getFolderList(params);
                    }

                    data = response.data;
                }

                const { folders, files } = this.parseFolderContentItems(data.items || []);

                this.folderItems = folders;
                this.fileItems = this.isSaveMode ? files : [];
                this.totalCount = data.total ?? data.totalCount ?? folders.length + files.length;

                if (!this.useServerPagination) {
                    this.selectedFolderId = null;
                }

            } catch (error) {
                if (error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
                    console.error('Error loading folders:', error);

                    this.$bvToast.toast('Failed to load folders', {
                        title: 'Error',
                        variant: 'danger',
                        solid: true,
                        autoHideDelay: 3000
                    });
                }
            } finally {
                this.isListLoading = false;
            }
        },

        selectFolder(folder) {
            this.selectedFolderId = folder.id;
            this.$emit('folder-selected', folder);
        },

        navigateToFolder(folderId) {
            const folder = this.folderItems.find(item => item.id === folderId);

            if (!folder) {
                return;
            }

            this.internalFolderId = folderId;
            this.currentPage = 1;
            this.selectedFolderId = folderId;
            this.fileItems = [];

            this.breadcrumbs.push({
                id: folderId,
                text: folder.name
            });

            this.$emit('folder-selected', folder);
            this.loadFolders();
        },

        goToFolder(breadcrumbData) {
            const { index, id } = typeof breadcrumbData === 'object' ? breadcrumbData : { index: arguments[1], id: breadcrumbData };

            if (index === 0 || !id) {
                this.internalFolderId = null;
                this.breadcrumbs = [];
                this.$emit('folder-selected', null);
            } else {
                this.internalFolderId = id;
                this.breadcrumbs = this.breadcrumbs.slice(0, index);
                const currentFolder = this.breadcrumbs[this.breadcrumbs.length - 1];
                this.$emit('folder-selected', currentFolder ? { id: currentFolder.id, name: currentFolder.text } : null);
            }

            this.currentPage = 1;
            this.selectedFolderId = null;
            this.fileItems = [];
            this.loadFolders();
        }
    }
}
</script>

<style scoped lang="scss">
.copy-assets-destination-view {
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: 60vh;

  .folder-sticky-header {
    position: sticky;
    top: 0;
    background: #fff;
    z-index: 4;
  }

  .folder-scroll-area {
    overflow-y: auto;
    min-height: 280px;
    flex: 1 1 auto;
    padding-right: 4px;
  }

  .folder-sticky-footer {
    position: sticky;
    bottom: 0;
    background: #fff;
    z-index: 4;
    padding-top: 8px;
  }

  ::v-deep .breadcrumb {
    background: unset;
    border-radius: unset;
    border: unset;
  }

  .section-container {
    margin-bottom: 0;
  }

  .folders-grid {
    grid-template-rows: auto;
    justify-items: center;
    margin-bottom: 1rem;
  }

  .search-container {
    margin-bottom: 0;
  }

  .search-input-wrapper {
    position: relative;
    width: 100%;
  }

  .search-input {
    width: 100%;
    height: 35px;
    background: white;
    border: 1px solid #b1b1b1;
    border-radius: 12px;
    padding: 0 35px 0 10px;
    outline: 0;
    font-family: "Nunito", sans-serif;
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    color: #464646;
  }

  .search-icon {
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
  }

  .clear-search-btn {
    position: absolute;
    right: 35px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    cursor: pointer;
    color: #727272;
    padding: 0;
    font-size: 14px;

    &:hover {
      color: #333;
    }
  }

  .item-pagination ul {
    margin-bottom: 0 !important;
  }

  .c-pagination {
    width: fit-content;
    margin: .75rem auto;

    .page-item {
      .page-link {
        font-family: "Nunito", sans-serif;
        font-style: normal;
        font-weight: 700;
        font-size: 14px;
        color: #727272;
        border: none;
      }

      .page-link:hover {
        background-color: #95d9cc;
      }

      .page-link:focus {
        box-shadow: unset;
      }
    }

    .page-item.active {
      .page-link {
        background-color: #007964;
        color: white;
      }
    }
  }
}
</style>
